import React from 'react';
import { Row, Col, Typography } from '@lucid/lego';

/**
 * Props for the FieldDisplay component
 */
type FieldDisplayProps = {
  /** The label text to display */
  label: string;
  /** The value to display */
  value: string;
  /** The theme for styling the field display */
  theme: 'light' | 'dark';
};

/**
 * Field Display Component
 * Displays a label-value pair in a consistent format
 *
 * @component
 * @param {FieldDisplayProps} props - Component props
 * @param {string} props.label - The label text to display
 * @param {string} props.value - The value to display
 * @param {'light' | 'dark'} props.theme - The theme for styling the field display
 * @returns {JSX.Element} Field display with label and value
 * @example
 * <FieldDisplay label="Status" value="Completed" theme="light" />
 */
export const FieldDisplay: React.FC<FieldDisplayProps> = ({ label, value, theme }) => {
  return (
    <Row data-testid="field-display" gutter={[8, 0]}>
      <Col lg={12}>
        <Typography variant="label" size="small" theme={theme} color="gray-7">
          {label}
        </Typography>
      </Col>
      <Col lg={12}>
        <Typography variant="body" size="extraSmall" theme={theme} color="black">
          {value}
        </Typography>
      </Col>
    </Row>
  );
};

FieldDisplay.displayName = 'FieldDisplay';
